import { useEffect, useRef } from 'react';

interface ProgressBarProps {
  currentStep: number;
  totalSteps: number;
  completedSteps?: number[];
  accessibleSteps?: number[];
  stepLabels?: string[];
  onStepClick?: (stepNumber: number) => void;
}

export function ProgressBar({
  currentStep,
  totalSteps,
  completedSteps = [],
  accessibleSteps = [],
  stepLabels = [],
  onStepClick
}: ProgressBarProps) {
  const progressPercentage = (currentStep / totalSteps) * 100;
  const activeStepRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    activeStepRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [currentStep]);

  return (
    <div className="w-full space-y-4">
      {/* Main progress bar */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-neutral-700">
            Étape {currentStep} sur {totalSteps}
          </p>
          <p className="text-sm font-medium text-primary-600">{Math.round(progressPercentage)}%</p>
        </div>

        <div className="progress-bar shadow-sm">
          <div
            className="progress-bar-fill"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      {/* Step navigation (optional) */}
      {stepLabels.length > 0 && (
        <div className="overflow-x-auto pb-1 scrollbar-hide pt-4">
          <div className="flex items-center sm:justify-center gap-2">
          {stepLabels.map((label, index) => {
            const stepNumber = index + 1;
            const isCompleted = completedSteps.includes(stepNumber);
            const isCurrent = stepNumber === currentStep;
            const isAccessible = stepNumber <= currentStep || isCompleted || accessibleSteps.includes(stepNumber);

            return (
              <button
                key={stepNumber}
                ref={isCurrent ? activeStepRef : null}
                onClick={() => onStepClick && isAccessible && onStepClick(stepNumber)}
                disabled={!isAccessible}
                className={`flex-none flex flex-col items-center gap-1 py-2 px-3 text-xs rounded-md transition-colors min-w-[56px] ${
                  isCurrent
                    ? 'bg-primary-50 text-primary-700'
                    : isCompleted
                    ? 'bg-success-50 text-success-700'
                    : isAccessible
                    ? 'bg-success-50 text-success-700'
                    : 'bg-neutral-50 text-neutral-400 cursor-not-allowed'
                }`}
              >
                <div className="font-medium text-sm">{stepNumber}</div>
                <div className="text-center leading-tight max-w-full break-words">{label}</div>
              </button>
            );
          })}
          </div>
        </div>
      )}
    </div>
  );
}